<script lang="ts">
  import NProgress from 'nprogress';

  import { Toaster } from '@significa/svelte-toaster';

  import { toaster } from '$lib/toaster';
  import Banner, { bannerInfo } from '$components/banner.svelte';
  import Gtm from '$components/gtm.svelte';
  import Navbar from '$components/navigation/navbar.svelte';
  import Footer from '$components/footer.svelte';
  import FittingBox from '$components/fittingbox.svelte';
  import KlarnaMessaging from '$components/klarna-messaging.svelte';
  import ToastNotification from '$components/toast-notification.svelte';
  import RegionNudge from '$components/region-nudge.svelte';
  import { page } from '$app/stores';
  import { afterNavigate, beforeNavigate } from '$app/navigation';
  import { onMount } from 'svelte';
  import { user } from '$lib/stores/user';
  import Alternates from '$components/alternates.svelte';
  import Authentication from '$components/navigation/auth/authentication.svelte';
  import { cn } from '$lib/utils.js';
  import CartDrawer from '$components/cart/cart-drawer.svelte';
  import ProgressBar from '$components/progress-bar.svelte';
  import { fly } from 'svelte/transition';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media.js';
  import { drawer } from '$lib/stores/drawer.js';
  import { CART_DRAWER_ID } from '$lib/constants.js';
  import CookiesModal from '$components/cookies-modal.svelte';
  import { region } from '$lib/region/index.js';
  import { abTestVersion, isABTestVersion } from '$lib/stores/ab-test-version';
  import { analyticsEvent, AnalyticsEvents } from '$lib/analytics';
  import { progressBarConfig } from '$lib/stores/campaigns.js';

  export let data;

  NProgress.configure({
    template: `<div role="bar" class="pointer-events-none bg-accent fixed z-[99999] top-0 left-0 w-full h-0.5 shadow-[0_0_10px_hsl(var(--color-pink)/0.5)]"></div>`
  });
  beforeNavigate(() => {
    NProgress.start();
  });

  afterNavigate(() => {
    NProgress.done();
  });

  onMount(() => {
    //Will validate current access token cookie and fill the user store with or without the user data
    user.refresh();
    showProgress = true;

    // set ab test globally
    abTestVersion.set(data.ab_test_version);
    window.abTestVersion = data.ab_test_version;

    if (isABTestVersion(data.ab_test_version)) {
      analyticsEvent({
        event: AnalyticsEvents.PAGE_VIEW,
        test_group: data.ab_test_version
      });
    }
  });

  // If screen height is less than 667px, it's a small device
  let screenSize = 0;

  // Get the current page region and language, and remove them from the path.
  // This is used to check if the current page is in the list of pages that should not have a sticky banner
  $: pageRegion = $page.params.region;
  $: pageLang = $page.params.lang;
  $: pagePath = $page.url.pathname.replace(`/${pageRegion}/${pageLang}`, '');
  $: exceptThisPage =
    (data.configuration.content.except_sticky !== '' &&
      data.configuration.content.except_sticky
        ?.split(',')
        ?.some((page: string) => pagePath?.includes(page))) ||
    false;
  $: isBannerSticky = (data.configuration.content.is_sticky && !exceptThisPage) || false;
  $: bannerInfo.update((prev) => ({ ...prev, isSticky: isBannerSticky }));
  $: showCookies = data.configuration.content.show_cookies || [];

  const lg = createBreakpointMediaQueryStore('lg', 'max-width');
  let showProgress = false;
</script>

<Alternates />
<Gtm {showCookies} />
<KlarnaMessaging />

<Toaster position="top-center" inset="sm" gap="sm" {toaster} let:toast>
  <ToastNotification {toast} />
</Toaster>

<RegionNudge />
<Banner items={data.configuration.content.banner_items || []} {isBannerSticky} />
<Authentication />
<CartDrawer />
<FittingBox />
<Navbar
  configuration={data.configuration.content}
  {isBannerSticky}
  abTestVersion={data.ab_test_version}
/>
{#if showCookies.includes($region)}
  <CookiesModal />
{/if}
{#if $progressBarConfig && !$progressBarConfig.hideSaleComponents && $lg && showProgress}
  <div
    class="fixed bottom-0 left-0 right-0 rounded-t-md bg-highlight shadow-top-inverted p-1.5 z-40"
    transition:fly={{ duration: 500, delay: 100, y: '100%', opacity: 100 }}
  >
    <ProgressBar on:click={() => drawer.open(CART_DRAWER_ID)} showConfettiAnimation={true} />
  </div>
{/if}

<svelte:window bind:innerHeight={screenSize} />

<div id="drawer" />

<slot />

<Footer
  configuration={data.configuration.content}
  class={cn($page.route.id?.includes('account/') && 'lg:block hidden')}
/>

<div aria-hidden="true" class="lg:pt-0 pt-9"></div>
